import React, { useState, useEffect } from 'react';
import PositionSelector, { Position } from './PositionSelector';

interface PositionFilterProps {
  filterValue: string[];
  updateFilters: (value: string[]) => void;
  disabled?: boolean;
  label?: string;
  quickFilterPositionIds?: string[]; // Optional list of position IDs to limit available options
}

const PositionFilter: React.FC<PositionFilterProps> = ({
  filterValue,
  updateFilters,
  disabled = false,
  label = 'Positions',
  quickFilterPositionIds,
}) => {
  const [positions, setPositions] = useState<Position[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    // Load positions from both SPD and Local Chapter positions
    const loadPositions = async () => {
      try {
        setLoading(true);

        // In a real implementation, these would be API calls
        // For now, we'll create mock data based on the position IDs from the documentation
        const mockPositions: Position[] = [
          // SPD Officers
          {
            id: '9106b4e431cf409da0823b1e7835bd48',
            order: 40,
            name: 'S/P/D President',
          },
          {
            id: 'f3a01279645945748754897d2e572a7e',
            order: 41,
            name: 'S/P/D Vice President',
          },
          {
            id: '5b1e6163236443068d07183eace13bbe',
            order: 43,
            name: 'S/P/D Organizer',
          },
          {
            id: '002ad10712bb48b3b08334b26e57491a',
            order: 45,
            name: 'S/P/D Treasurer',
          },
          {
            id: 'a0d6b40f5a0f4214ac928f2f0b3e77b9',
            order: 47,
            name: 'S/P/D Secretary',
          },

          // SPD Appointed Positions
          {
            id: 'b51dd739d83b4583ab9444b788a8bcdd',
            order: 48,
            name: 'S/P/D Executive Secretary/Treasurer',
          },
          {
            id: '38b8ab2ae9034d499ceda0f9bc49717e',
            order: 49,
            name: 'S/P/D Executive Treasurer',
          },
          {
            id: '4fd3141264664a7798b3675be4f45c78',
            order: 50,
            name: 'S/P/D Executive Secretary',
          },

          // Local Chapter Officers
          {
            id: 'c8094b8d6c514c219f4a5bbdd2c72b6b',
            order: 70,
            name: 'Local Chapter President',
          },
          {
            id: '98619508202847c8adc15a4b11a079f7',
            order: 71,
            name: 'Local Chapter Vice President',
          },
          {
            id: '070e1690d8de4854bb22a3b35bd3742b',
            order: 72,
            name: 'Local Chapter Recording Secretary',
          },
          {
            id: 'dc10f6cbd8e94070bb73b4685b5e84b8',
            order: 73,
            name: 'Local Chapter Corresponding Secretary',
          },
          {
            id: 'cc32bed8f4784291b0424e830f0c8cc7',
            order: 74,
            name: 'Local Chapter Treasurer',
          },
          {
            id: '888c4b116aae46e496c1f2c8ad9feb34',
            order: 75,
            name: 'Local Chapter Chaplain',
          },
          {
            id: '8af77101465e4650a6af36bbc183f142',
            order: 76,
            name: 'Local Chapter Guard',
          },
          {
            id: '0063de12834544deaebed87d985638ff',
            order: 77,
            name: 'Local Chapter Membership Chair',
          },
          {
            id: '4f55953df0b04bc9b76a775c420a8460',
            order: 78,
            name: 'Local Chapter Technology Contact',
          },

          // Local Chapter Chairs
          {
            id: '3a1be16c08124e2f9c0c9a586300f788',
            order: 79,
            name: 'Local ELF Chair',
          },
          {
            id: 'c0c110710f584e6ead6677c85945bd14',
            order: 80,
            name: 'Local IPS Chair',
          },
          {
            id: '8b929bff2721444c89c40bff12a5a281',
            order: 82,
            name: 'Local PSA Chair',
          },
          {
            id: 'c49c712623d64de381c6d9bf37f28f8a',
            order: 82,
            name: 'Local PCE Chair',
          },
          {
            id: 'ff1db84ddec34608afa38d7895bafee9',
            order: 83,
            name: 'Local STAR Chair',
          },
          {
            id: 'a72ce9faa02144b6b4895d42b3033d0a',
            order: 84,
            name: 'Local Cottey College Chair',
          },
        ];

        // Store all positions, filtering will happen in render
        setPositions(mockPositions);
        setLoading(false);
      } catch (error) {
        console.error('Error loading positions:', error);
        setLoading(false);
      }
    };

    loadPositions();
  }, []); // Only load once

  const handleChange = (value: string | string[] | null) => {
    updateFilters(Array.isArray(value) ? value : value ? [value] : []);
  };

  if (loading) {
    return <div>Loading positions...</div>;
  }

  // Apply filtering on every render to ensure it's always up to date
  const getFilteredPositions = () => {
    if (!quickFilterPositionIds || quickFilterPositionIds.length === 0) {
      return positions;
    }

    return positions.filter((position) =>
      quickFilterPositionIds.includes(position.id)
    );
  };

  const filteredPositions = getFilteredPositions();

  return (
    <PositionSelector
      value={filterValue}
      onChange={handleChange}
      positions={filteredPositions}
      multiple={true}
      label={label}
      disabled={disabled}
      placeholder="Select positions to filter by..."
    />
  );
};

export default PositionFilter;
